import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const FIRMAS = {
  'Essence Rawson': {
    nombre: 'Essence Rawson',
    direccion: 'Rawson 1457',
  },
  'Essence Castelli': {
    nombre: 'Essence Castelli',
    direccion: 'Castelli 1515',
  },
}

function DetalleModal({ difusion, onClose }) {
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 24px', overflowY: 'auto' }} onClick={onClose}>
      <div className="card fade-in" style={{ maxWidth: 640, width: '100%', borderColor: 'var(--gold)', borderWidth: 1.5, marginBottom: 40 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 6 }}>DIFUSIÓN ENVIADA</div>
            <h2 style={{ fontSize: 22, fontFamily: 'Cormorant Garamond, serif', fontWeight: 700, marginBottom: 4 }}>{difusion.asunto}</h2>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              {new Date(difusion.created_at).toLocaleString('es-AR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
              {' · '}{difusion.usuarios?.nombre}
              {' · '}{difusion.total_enviados} destinatario{difusion.total_enviados !== 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 22, padding: 4 }}>✕</button>
        </div>
        <div style={{ fontSize: 14, color: 'var(--text2)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{difusion.mensaje}</div>
      </div>
    </div>,
    document.body
  )
}

export default function Difusiones() {
  const { usuario } = useAuth()
  const [difusiones, setDifusiones] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [edificios, setEdificios] = useState([])
  const [edificioSeleccionado, setEdificioSeleccionado] = useState(null)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [selectedDifusion, setSelectedDifusion] = useState(null)
  const [preview, setPreview] = useState(false)
  const [form, setForm] = useState({ asunto: '', mensaje: '' })

  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : usuario?.edificio_id

  useEffect(() => {
    fetchEdificios()
    fetchDifusiones()
  }, [])

  useEffect(() => {
    if (edificioSeleccionado) fetchOficinas()
  }, [edificioSeleccionado])

  const fetchEdificios = async () => {
    let q = supabase.from('edificios').select('*')
    if (edificioId) q = q.eq('id', edificioId)
    const { data } = await q
    setEdificios(data || [])
    if (!isAdmin && data?.length > 0) setEdificioSeleccionado(data[0])
    else if (isAdmin && data?.length > 0) setEdificioSeleccionado(null)
  }

  const fetchOficinas = async () => {
    if (!edificioSeleccionado) return
    const { data } = await supabase.from('oficinas')
      .select('id, numero, titular, email_contacto, email_contacto_2')
      .eq('edificio_id', edificioSeleccionado.id)
      .eq('activa', true)
      .order('numero')
    setOficinas(data || [])
  }

  const fetchDifusiones = async () => {
    setLoading(true)
    let q = supabase.from('difusiones')
      .select('*, edificios(nombre), usuarios(nombre)')
      .order('created_at', { ascending: false })
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setDifusiones(data || [])
    setLoading(false)
  }

  const handleEdificioSelect = (edificio) => {
    setEdificioSeleccionado(edificio)
  }

  const handleSend = async () => {
    if (!form.asunto.trim() || !form.mensaje.trim() || !edificioSeleccionado) return
    if (oficinas.length === 0) return
    setSending(true)

    const firma = FIRMAS[edificioSeleccionado.nombre] || { nombre: edificioSeleccionado.nombre, direccion: '' }

    // Enviar a cada oficina con email
    let enviados = 0
    for (const oficina of oficinas) {
      const emails = [oficina.email_contacto, oficina.email_contacto_2].filter(Boolean)
      for (const emailDest of emails) {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'difusion',
          destinatario_email: emailDest,
          oficina: oficina.numero,
          oficina_titular: oficina.titular,
          edificio: edificioSeleccionado.nombre,
          datos: {
            asunto: form.asunto,
            mensaje: form.mensaje,
            firma_nombre: firma.nombre,
            firma_direccion: firma.direccion,
          }
        })
      })
      if (res.ok) enviados++
      }
    }

    // Guardar en historial
    await supabase.from('difusiones').insert({
      edificio_id: edificioSeleccionado.id,
      usuario_id: usuario?.id,
      asunto: form.asunto.trim(),
      mensaje: form.mensaje.trim(),
      total_enviados: enviados,
    })

    setSuccess(`Difusión enviada a ${enviados} oficina${enviados !== 1 ? 's' : ''}`)
    setTimeout(() => setSuccess(null), 4000)
    setForm({ asunto: '', mensaje: '' })
    setShowForm(false)
    setPreview(false)
    fetchDifusiones()
    setSending(false)
  }

  const formatDate = (ts) => new Date(ts).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })
  const formatTime = (ts) => new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })

  const edificioActual = edificioSeleccionado
  const firma = edificioActual ? (FIRMAS[edificioActual.nombre] || { nombre: edificioActual.nombre, direccion: '' }) : null

  return (
    <div className="fade-in">
      {success && (
        <div style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--green)', color: 'white', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', whiteSpace: 'nowrap' }}>
          ✓ {success}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 36, color: 'var(--text)', marginBottom: 4 }}>Difusiones</h1>
          <p style={{ color: 'var(--text3)', fontSize: 14 }}>Comunicados por email a todas las oficinas</p>
        </div>
        <button className="btn btn-gold" onClick={() => { setShowForm(!showForm); setPreview(false) }}>
          {showForm ? '✕ Cancelar' : '+ Nueva difusión'}
        </button>
      </div>

      {/* Selector de edificio (admin) */}
      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {edificios.map(e => (
            <button key={e.id}
              className={`btn btn-sm ${edificioSeleccionado?.id === e.id ? 'btn-gold' : 'btn-ghost'}`}
              onClick={() => handleEdificioSelect(e)}>
              {e.nombre.replace('Essence ', '')}
            </button>
          ))}
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <div className="card fade-in" style={{ marginBottom: 24, borderColor: 'var(--gold)', borderWidth: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>
              NUEVA DIFUSIÓN · {edificioActual?.nombre?.toUpperCase()} · {oficinas.length} OFICINAS CON EMAIL
            </div>
            <button className={`btn btn-sm ${preview ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setPreview(!preview)}>
              {preview ? '✏️ Editar' : '👁 Vista previa'}
            </button>
          </div>

          {!edificioSeleccionado && isAdmin ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text3)' }}>
              Seleccioná un edificio para continuar
            </div>
          ) : !preview ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>ASUNTO</label>
                <input className="input" placeholder="Ej: Aviso de corte de agua programado"
                  value={form.asunto} onChange={e => setForm(f => ({ ...f, asunto: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>MENSAJE</label>
                <textarea className="input" placeholder="Escribí el comunicado..."
                  value={form.mensaje} onChange={e => setForm(f => ({ ...f, mensaje: e.target.value }))}
                  style={{ minHeight: 160, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.6 }} />
              </div>
            </div>
          ) : (
            /* Vista previa del email */
            <div style={{ background: '#1A1A16', borderRadius: 10, padding: 24, border: '1px solid var(--border)' }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <img src="/logo.png" alt="Edificios Essence" style={{ width: 70, marginBottom: 12 }} />
                <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>{edificioActual?.nombre}</div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 20, marginBottom: 20 }}>
                <div style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 8 }}>ASUNTO</div>
                <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>{form.asunto || 'Sin asunto'}</div>
                <div style={{ fontSize: 14, color: 'var(--text2)', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{form.mensaje || 'Sin mensaje'}</div>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, fontSize: 12, color: 'var(--text3)' }}>
                <div style={{ fontWeight: 600, color: 'var(--text2)', marginBottom: 2 }}>Equipo de administración de Edificios Essence</div>
                <div>{firma?.nombre}</div>
                <div>{firma?.direccion}</div>
              </div>
            </div>
          )}

          {edificioSeleccionado && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 20, alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                {(() => {
                const conEmail = oficinas.filter(o => o.email_contacto || o.email_contacto_2)
                const conSegundo = oficinas.filter(o => o.email_contacto_2)
                return <>
                  Se enviará a <strong style={{ color: 'var(--gold)' }}>{conEmail.length}</strong> oficina{conEmail.length !== 1 ? 's' : ''} de {edificioActual?.nombre}
                  {conSegundo.length > 0 && <span style={{ color: 'var(--text3)', marginLeft: 4 }}>· {conSegundo.length} con segundo email</span>}
                </>
              })()}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-outline btn-sm" onClick={() => { setShowForm(false); setPreview(false) }}>Cancelar</button>
                <button className="btn btn-gold btn-sm" onClick={handleSend}
                  disabled={sending || !form.asunto.trim() || !form.mensaje.trim() || oficinas.length === 0}>
                  {sending ? 'Enviando...' : '📨 Enviar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Historial */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>
          HISTORIAL · {difusiones.length} difusión{difusiones.length !== 1 ? 'es' : ''} enviada{difusiones.length !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>Cargando...</div>
      ) : difusiones.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '64px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📨</div>
          <div style={{ color: 'var(--text3)', fontSize: 14 }}>No hay difusiones enviadas todavía</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {difusiones.map(d => (
            <div key={d.id} className="card" style={{ padding: '16px 20px', cursor: 'pointer', transition: 'border-color 0.15s' }}
              onClick={() => setSelectedDifusion(d)}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                  📨
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{d.asunto}</span>
                    {isAdmin && d.edificios && (
                      <span className="badge badge-gold" style={{ fontSize: 10 }}>{d.edificios.nombre.replace('Essence ', '')}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                    {d.usuarios?.nombre} · {d.total_enviados} destinatario{d.total_enviados !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 12, color: 'var(--text2)' }}>{formatDate(d.created_at)}</div>
                  <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--text3)' }}>{formatTime(d.created_at)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedDifusion && <DetalleModal difusion={selectedDifusion} onClose={() => setSelectedDifusion(null)} />}
    </div>
  )
}
